import { useState, useEffect, useCallback, useRef } from 'react';
import { GridColumnResizeEvent, GridColumnReorderEvent } from '@progress/kendo-react-grid';

interface SavedColumnSetting { field: string; width?: string | number; orderIndex: number; }

export const useGridColumnPersist = (localStorageKey: string, baseColumns: any[]) => {
    const [columns, setColumns] = useState<any[]>(baseColumns);
    const prevColumnsJsonRef = useRef('');

    const saveToLocalStorage = useCallback((currentColumns: any[]) => {
        const settingsToSave: SavedColumnSetting[] = currentColumns
            .filter((col) => col.field)
            .map((col, index) => ({ field: col.field, width: col.width, orderIndex: index }));
        if (settingsToSave.length > 0) {
            localStorage.setItem(localStorageKey, JSON.stringify(settingsToSave));
        }
    }, [localStorageKey]);

    useEffect(() => {
        const savedSettingsStr = localStorage.getItem(localStorageKey);
        let newColumns: any[] = [];
        if (savedSettingsStr) {
            try {
                const savedSettings: SavedColumnSetting[] = JSON.parse(savedSettingsStr);
                const baseColumnsMap = new Map(baseColumns.map((col) => [col.field, col]));
                const sortedSettings = savedSettings.sort((a, b) => a.orderIndex - b.orderIndex);
                sortedSettings.forEach((setting) => {
                    const baseCol = baseColumnsMap.get(setting.field);
                    if (baseCol) {
                        newColumns.push({ ...baseCol, width: setting.width || baseCol.width });
                        baseColumnsMap.delete(setting.field);
                    }
                });
                baseColumnsMap.forEach((col) => newColumns.push(col));
            } catch (e) { console.error(e); newColumns = baseColumns; }
        } else {
            newColumns = baseColumns;
            saveToLocalStorage(baseColumns);
        }
        const newColumnsJson = JSON.stringify(newColumns);
        if (newColumnsJson !== prevColumnsJsonRef.current) {
            prevColumnsJsonRef.current = newColumnsJson;
            setColumns(newColumns);
        }
    }, [baseColumns, localStorageKey, saveToLocalStorage]);

    const onColumnResize = useCallback((event: GridColumnResizeEvent) => {
        if (event.end) {
            setColumns((prev) => {
                const next = prev.map(col => {
                    const match = event.columns.find((c) => c.field === col.field);
                    return match ? { ...col, width: match.width } : col;
                });
                saveToLocalStorage(next);
                prevColumnsJsonRef.current = JSON.stringify(next);
                return next;
            });
        }
    }, [saveToLocalStorage]);

    const onColumnReorder = useCallback((event: GridColumnReorderEvent) => {
        setColumns((prevColumns) => {
            const columnsFromEvent = event.columns.map((c: any) => ({
                field: c.field,
                orderIndex: c.orderIndex
            }));

            columnsFromEvent.sort((a, b) => a.orderIndex - b.orderIndex);

            const newOrderFields = columnsFromEvent.map(c => c.field);
            const currentMap = new Map(prevColumns.map(c => [c.field, c]));

            const nextColumns = newOrderFields
                .map(field => currentMap.get(field))
                .filter(c => c !== undefined);

            if (nextColumns.length < prevColumns.length) {
                prevColumns.forEach(col => {
                    if (!col.field || !newOrderFields.includes(col.field)) {
                        nextColumns.push(col);
                    }
                });
            }

            saveToLocalStorage(nextColumns);
            prevColumnsJsonRef.current = JSON.stringify(nextColumns);

            return nextColumns;
        });
    }, [saveToLocalStorage]);

    return { columns, onColumnResize, onColumnReorder };
};
